import React, { useEffect, useState, useContext } from 'react';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { UserContext } from '../../contexts/UserContext';
import { useNavigate, Link } from 'react-router-dom';
import '../../App.css';

const SavedItems = ({ username }) => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [savedContent, setSavedContent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Only show if it's the user's own profile
  const isOwnProfile = user?.email?.split('@')[0] === username;

  useEffect(() => {
    const fetchSavedItems = async () => {
      if (!isOwnProfile || !user?.uid) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Get user's saved items list
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) {
          setIsLoading(false);
          return;
        }

        const userData = userDoc.data();
        const savedItemIds = userData.savedItems || [];
        
        if (savedItemIds.length === 0) {
          setSavedContent([]);
          setIsLoading(false);
          return;
        }

        console.log('User has saved items:', savedItemIds);

        const resolvedContent = [];

        // For each saved item ID, find the actual content
        for (const itemId of savedItemIds) {
          try {
            // Check if it's a post (no hyphen in ID)
            if (!itemId.includes('-')) {
              const postDoc = await getDoc(doc(db, 'posts', itemId));
              if (postDoc.exists()) {
                const postData = postDoc.data();
                resolvedContent.push({
                  id: itemId,
                  type: 'post',
                  imageUrl: postData.imageUrl,
                  description: postData.description,
                  username: postData.username,
                  createdAt: postData.createdAt,
                  savedAt: new Date(), // Could store actual save timestamp
                  originalData: postData
                });
                continue;
              }
            }

            // Handle legacy items (photos-0, videos-1, legacy-photo-0, etc.)
            if (itemId.includes('-')) {
              const parts = itemId.split('-');
              let contentType, index, itemUsername;

              // Handle different legacy formats
              if (parts.length === 2) {
                // Format: "photos-0", "videos-1"
                [contentType, index] = parts;
                itemUsername = username; // Assume it's the current user's content
              } else if (parts.length === 3 && parts[0] === 'legacy') {
                // Format: "legacy-photo-0"
                contentType = parts[1] + 's'; // "photo" -> "photos"
                index = parts[2];
                itemUsername = username;
              } else {
                console.log(`Unknown legacy format: ${itemId}`);
                continue;
              }

              const indexNum = parseInt(index);

              // Get the user's data to access their legacy content
              let targetUserData = userData; // Start with current user
              
              // If it's not the current user's content, we'd need to fetch that user
              // For now, assume it's the current user since we're in their saved items
              
              let contentArray = [];
              if (contentType === 'photos') {
                contentArray = targetUserData.photos || [];
              } else if (contentType === 'videos') {
                contentArray = targetUserData.videos || [];
              }

              if (indexNum >= 0 && indexNum < contentArray.length) {
                const contentUrl = contentArray[indexNum];
                if (contentUrl && contentUrl.trim() !== '') {
                  resolvedContent.push({
                    id: itemId,
                    type: contentType === 'photos' ? 'legacy-photo' : 'legacy-video',
                    imageUrl: contentUrl,
                    description: `${contentType === 'photos' ? 'Photo' : 'Video'} from @${itemUsername}`,
                    username: itemUsername,
                    createdAt: null, // Legacy items don't have timestamps
                    savedAt: new Date(),
                    originalData: { url: contentUrl }
                  });
                }
              }
            }

          } catch (error) {
            console.error(`Error resolving saved item ${itemId}:`, error);
          }
        }

        // Sort by saved date (newest first), then by creation date
        resolvedContent.sort((a, b) => {
          // First sort by saved date
          const savedDateA = a.savedAt || new Date(0);
          const savedDateB = b.savedAt || new Date(0);
          if (savedDateA.getTime() !== savedDateB.getTime()) {
            return savedDateB - savedDateA;
          }
          
          // Then by creation date
          const createdA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt || new Date(0));
          const createdB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt || new Date(0));
          return createdB - createdA;
        });

        setSavedContent(resolvedContent);

      } catch (error) {
        console.error('Error fetching saved items:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSavedItems();
  }, [username, user, isOwnProfile]);

  const handleContentClick = (item) => {
    // Navigate to the original user's profile with the item
    navigate(`/profiles/${item.username}/${item.id}`);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getContentTypeLabel = (item) => {
    switch (item.type) {
      case 'post':
        return 'Post';
      case 'legacy-photo':
        return 'Photo';
      case 'legacy-video':
        return 'Video';
      default:
        return 'Content';
    }
  };

  if (!isOwnProfile) {
    return null; // Don't show saved items for other users
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '27.2px', color: '#666' }}>
        <p>Loading saved items...</p>
      </div>
    );
  }

  if (savedContent.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '27.2px', color: '#666' }}>
        <p>No saved items yet.</p>
        <p style={{ fontSize: '9.52px', marginTop: '5.44px' }}>
          Items you save will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="discover-grid">
      {savedContent.map((item, index) => (
        <div 
          key={`${item.id}-${index}`}
          className="discover-card"
          style={{
            position: 'relative',
            cursor: 'pointer'
          }}
          onClick={() => handleContentClick(item)}
          onMouseEnter={() => setHoveredItem(`${item.id}-${index}`)}
          onMouseLeave={() => setHoveredItem(null)}
        >
          {item.type === 'legacy-video' ? (
            <video
              src={item.imageUrl}
              className="card-image"
              style={{
                width: '100%',
                height: '476px',
                objectFit: 'cover',
                borderRadius: '8.16px'
              }}
              muted
              loop
              playsInline
              onMouseOver={e => e.target.play()}
              onMouseOut={e => e.target.pause()}
              controls={false}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <img 
              src={item.imageUrl} 
              alt={item.description || 'Saved content'} 
              className="card-image"
              style={{
                width: '100%',
                height: '476px',
                objectFit: 'cover',
                borderRadius: '8.16px'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          
          {/* Hover overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.8) 100%)',
            opacity: hoveredItem === `${item.id}-${index}` ? 1 : 0,
            transition: 'opacity 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            padding: '13.6px',
            borderRadius: '8.16px'
          }}>
            {/* Content info */}
            <div style={{ marginBottom: '8.16px' }}>
              <div style={{
                color: 'white',
                fontSize: '9.52px',
                fontWeight: 600,
                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                marginBottom: '2.72px'
              }}>
                @{item.username}
              </div>
              {item.description && (
                <div style={{
                  color: 'white',
                  fontSize: '8.84px',
                  textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                  lineHeight: '1.3',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {item.description}
                </div>
              )}
            </div>
            
            {/* Content type and saved indicator */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5.44px'
              }}>
                <div style={{
                  color: '#0d47a1',
                  fontSize: '10.88px',
                  textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                }}>
                  ★
                </div>
                <div style={{
                  color: 'white',
                  fontSize: '8.16px',
                  textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                }}>
                  Saved
                </div>
              </div>
              
              {/* Content type badge */}
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '1.36px 4.08px',
                borderRadius: '2.72px',
                fontSize: '6.8px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.34px'
              }}>
                {getContentTypeLabel(item)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SavedItems;